import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { css } from "styled-system/css";
import { api } from "~/lib/api";
import { Link } from "react-router";
import { fmtDate, fmtNum, pluralWord } from "~/lib/format";
import { Ledger } from "~/components/charts";
import { ed, btn, tbl } from "~/components/editorial";

// --- Types matching new API shape ---

interface ScheduleItem {
  name: string;
  status: "Completed" | "In Progress" | "Not Started";
  start: string;
  end: string;
  relatedKeys: string[];
}

interface PDScheduleSection {
  id: string;
  name: string;
  items: ScheduleItem[];
}

interface PDScheduleData {
  sections: PDScheduleSection[];
  commentary: string;
  stats: {
    totalItems: number;
    completed: number;
    inProgress: number;
    notStarted: number;
  };
  jiraBaseUrl: string;
  generatedAt: string;
  cached: boolean;
}

// --- Status helpers ---

// Kept for the clipboard export only: the pasted board template colors
// status text. The page itself shows status in ink.
const statusColors: Record<string, string> = {
  Completed: "#22c55e",
  "In Progress": "#3b82f6",
  "Not Started": "#94a3b8",
};

function StatusLabel({ status }: { status: string }) {
  if (status === "In Progress") return <span className={tbl.label}>In progress</span>;
  if (status === "Completed") return <span className={tbl.tag}>✓ Completed</span>;
  return <span className={tbl.tag}>{status === "Not Started" ? "Not started" : status}</span>;
}

// --- Copy to clipboard helpers ---

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildClipboardText(
  sections: PDScheduleSection[],
  commentary: string | null,
): string {
  let text = "Product Development Schedule\n\n";

  if (commentary) {
    text += commentary + "\n\n";
  }

  for (const section of sections) {
    if (section.items.length === 0) continue;
    text += `${section.name}\n`;
    text += `${"Name".padEnd(60)}${"Start".padEnd(10)}${"End".padEnd(10)}Status\n`;
    text += "-".repeat(90) + "\n";
    for (const item of section.items) {
      text += `${item.name.padEnd(60)}${item.start.padEnd(10)}${item.end.padEnd(10)}${item.status}\n`;
    }
    text += "\n";
  }

  return text;
}

function buildClipboardHTML(
  sections: PDScheduleSection[],
  commentary: string | null,
): string {
  let html = "<h2>Product Development Schedule</h2>";

  if (commentary) {
    html += `<div style="margin-bottom:16px;white-space:pre-wrap">${escapeHtml(commentary).replace(/\n/g, "<br>")}</div>`;
  }

  for (const section of sections) {
    if (section.items.length === 0) continue;
    html += `<h3 style="margin-top:16px;margin-bottom:8px">${escapeHtml(section.name)}</h3>`;
    html +=
      '<table style="border-collapse:collapse;width:100%;font-size:13px">';
    html += '<thead><tr style="background:#f1f5f9">';
    html +=
      '<th style="text-align:left;padding:6px 12px;border:1px solid #e2e8f0">Name</th>';
    html +=
      '<th style="text-align:left;padding:6px 12px;border:1px solid #e2e8f0">Start</th>';
    html +=
      '<th style="text-align:left;padding:6px 12px;border:1px solid #e2e8f0">End</th>';
    html +=
      '<th style="text-align:left;padding:6px 12px;border:1px solid #e2e8f0">Status</th>';
    html += "</tr></thead><tbody>";
    for (const item of section.items) {
      const color = statusColors[item.status] ?? "#94a3b8";
      html += "<tr>";
      html += `<td style="padding:6px 12px;border:1px solid #e2e8f0">${escapeHtml(item.name)}</td>`;
      html += `<td style="padding:6px 12px;border:1px solid #e2e8f0">${escapeHtml(item.start)}</td>`;
      html += `<td style="padding:6px 12px;border:1px solid #e2e8f0">${escapeHtml(item.end)}</td>`;
      html += `<td style="padding:6px 12px;border:1px solid #e2e8f0"><span style="color:${color};font-weight:600">${escapeHtml(item.status)}</span></td>`;
      html += "</tr>";
    }
    html += "</tbody></table>";
  }

  return html;
}

// --- Main Component ---

export function PDSchedulePage() {
  const [copied, setCopied] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: scheduleData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["pd-schedule"],
    queryFn: () => api.get<{ data: PDScheduleData }>("/pm/pd-schedule"),
    staleTime: 5 * 60 * 1000,
  });

  const schedule = scheduleData?.data;

  const handleCopy = async () => {
    if (!schedule) return;
    const text = buildClipboardText(
      schedule.sections,
      schedule.commentary ?? null,
    );
    const html = buildClipboardHTML(
      schedule.sections,
      schedule.commentary ?? null,
    );

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([text], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const fresh = await api.get<{ data: PDScheduleData }>(
        "/pm/pd-schedule?refresh=true",
      );
      queryClient.setQueryData(["pd-schedule"], fresh);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <div className={ed.page}>
        <div className={ed.inner}>
          <Link to="/settings" className={ed.back}>
            ← Admin
          </Link>
          <div className={ed.loading}>Building the schedule from JIRA…</div>
        </div>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className={ed.page}>
        <div className={ed.inner}>
          <Link to="/settings" className={ed.back}>
            ← Admin
          </Link>
          <div className={ed.loading}>No schedule data available. Connect JIRA projects first.</div>
        </div>
      </div>
    );
  }

  const { stats } = schedule;
  const sections = schedule.sections.filter((s) => s.items.length > 0);
  const bySection = sections.map((section) => ({
    id: section.id,
    name: section.name,
    total: section.items.length,
    active: section.items.filter((i) => i.status === "In Progress").length,
    done: section.items.filter((i) => i.status === "Completed").length,
    upcoming: section.items.filter((i) => i.status === "Not Started").length,
  }));
  const busiest = [...bySection].sort((a, b) => b.active - a.active)[0];

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <Link to="/settings" className={ed.back}>
          ← Admin
        </Link>

        <div className={ed.kicker}>Board reporting · product development schedule</div>
        <h1 className={ed.headline}>
          {stats.totalItems === 0
            ? "No initiatives on the schedule"
            : `${fmtNum(stats.inProgress)} of ${fmtNum(stats.totalItems)} ${pluralWord(stats.totalItems, "initiative")} ${stats.inProgress === 1 ? "is" : "are"} in progress, ${fmtNum(stats.completed)} complete`}
        </h1>
        {stats.totalItems > 0 && (
          <p className={ed.dek}>
            {fmtNum(stats.notStarted)} {stats.notStarted === 1 ? "has" : "have"} not started.
            {busiest && busiest.active > 0
              ? ` ${busiest.name} has the most work moving, with ${fmtNum(busiest.active)} of its ${fmtNum(busiest.total)} in progress.`
              : ""}{" "}
            Across {fmtNum(sections.length)} {pluralWord(sections.length, "product line")}.
          </p>
        )}
        <div className={ed.bylineSplit}>
          <span>
            <b>Schedule</b> · initiatives and dates curated by a language model from JIRA epics and
            issues. Check dates before sending.
            {schedule.generatedAt && (
              <>
                {" "}
                Built <span className="num">{fmtDate(schedule.generatedAt)}</span>
                {schedule.cached ? " (cached)" : ""}.
              </>
            )}
          </span>
          <span className={styles.actions}>
            <button
              type="button"
              className={btn.primary}
              onClick={handleCopy}
              title="Copy schedule to clipboard (paste into Excel or Sheets)"
            >
              {copied ? "Copied" : "Copy for template"}
            </button>
            <button
              type="button"
              className={btn.secondary}
              onClick={() => handleRefresh()}
              disabled={isRefreshing}
              title="Build again, bypassing the cache"
            >
              {isRefreshing || isFetching ? "Building…" : "Refresh"}
            </button>
          </span>
        </div>

        <Ledger
          hideMissingPrior
          columns={4}
          items={[
            { label: "Initiatives", value: fmtNum(stats.totalItems), note: `${fmtNum(sections.length)} product lines` },
            { label: "In progress", value: fmtNum(stats.inProgress) },
            { label: "Completed", value: fmtNum(stats.completed) },
            { label: "Not started", value: fmtNum(stats.notStarted) },
          ]}
        />

        <div className={styles.main}>
          <div>
            {sections.map((section, si) => (
              <section key={section.id}>
                <div className={ed.sect} style={si === 0 ? { marginTop: 32 } : { marginTop: 28 }}>
                  {section.name} · <span className={ed.num}>{section.items.length}</span>{" "}
                  {pluralWord(section.items.length, "initiative")}
                </div>
                <div className={styles.tableWrap}>
                  <table className={tbl.table} style={{ tableLayout: "fixed" }}>
                    <colgroup>
                      <col />
                      <col style={{ width: 72 }} />
                      <col style={{ width: 72 }} />
                      <col style={{ width: 118 }} />
                      <col style={{ width: 210 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Initiative</th>
                        <th data-num>Start</th>
                        <th data-num>End</th>
                        <th>Status</th>
                        <th>Related</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.items.map((item, idx) => (
                        <tr key={`${section.id}-${idx}`}>
                          <td style={{ fontWeight: 600 }}>{item.name}</td>
                          <td data-num>{item.start}</td>
                          <td data-num>{item.end}</td>
                          <td>
                            <StatusLabel status={item.status} />
                          </td>
                          <td data-mono style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                            {item.relatedKeys.length > 0 && schedule.jiraBaseUrl ? (
                              <>
                                {item.relatedKeys.slice(0, 3).map((key, k) => (
                                  <span key={key}>
                                    {k > 0 ? ", " : ""}
                                    <a
                                      href={`${schedule.jiraBaseUrl}/browse/${key}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={`Open ${key} in Jira`}
                                      style={{ fontWeight: 400 }}
                                    >
                                      {key}
                                    </a>
                                  </span>
                                ))}
                                {item.relatedKeys.length > 3 && (
                                  <span className={ed.muted}> +{item.relatedKeys.length - 3}</span>
                                )}
                              </>
                            ) : (
                              <span className={ed.muted}>–</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>

          <aside className={styles.side}>
            <div className={ed.sect} style={{ marginTop: 32 }}>
              Commentary
            </div>
            <p className={ed.methodMono} style={{ marginTop: 6 }}>
              Written by a language model (Opus 4.6) from the schedule. Check before sending.
            </p>
            {isRefreshing ? (
              <div className={ed.loading} style={{ paddingTop: 12, paddingBottom: 12 }}>
                Writing commentary…
              </div>
            ) : schedule.commentary ? (
              <div className={styles.commentary}>
                {schedule.commentary
                  .split("\n")
                  .filter((line) => line.trim() !== "")
                  .map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
              </div>
            ) : (
              <p className={ed.small}>
                No commentary available. Set ANTHROPIC_API_KEY to enable it.
              </p>
            )}

            <div className={ed.sect} style={{ marginTop: 28 }}>
              By product line
            </div>
            <div className={styles.tableWrap}>
              <table className={tbl.table}>
                <thead>
                  <tr>
                    <th>Line</th>
                    <th data-num>Active</th>
                    <th data-num>Done</th>
                    <th data-num>Next</th>
                  </tr>
                </thead>
                <tbody>
                  {bySection.map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td data-num>
                        <span className={s.active ? undefined : ed.muted}>{s.active}</span>
                      </td>
                      <td data-num>
                        <span className={s.done ? undefined : ed.muted}>{s.done}</span>
                      </td>
                      <td data-num>
                        <span className={s.upcoming ? undefined : ed.muted}>{s.upcoming}</span>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ fontWeight: 700 }}>All</td>
                    <td data-num style={{ fontWeight: 700 }}>{stats.inProgress}</td>
                    <td data-num style={{ fontWeight: 700 }}>{stats.completed}</td>
                    <td data-num style={{ fontWeight: 700 }}>{stats.notStarted}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className={ed.methodMono}>Next: initiatives on the schedule that have not started.</p>
          </aside>
        </div>
      </div>
    </div>
  );
}

// --- Styles ---

const styles = {
  actions: css({ display: "inline-flex", gap: "8px" }),
  main: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", lg: "minmax(0, 1fr) 340px" },
    gap: "32px",
    alignItems: "start",
  }),
  side: css({ position: { lg: "sticky" }, top: "80px" }),
  tableWrap: css({ overflowX: "auto", mt: "4px" }),
  commentary: css({
    fontFamily: "serif",
    fontSize: "15.5px",
    lineHeight: 1.55,
    color: "ed.ink",
    "& p": { m: 0, mb: "10px" },
  }),
};
